<script>  import { onMount } from 'svelte';
  import Chart from 'chart.js/auto';
  import 'chartjs-adapter-date-fns';
  import { glucoseunit } from '$stores/unitsStore';
  import { convertGlucoseValue } from '$utils/unitUtils';
  import { selectedStudy } from '$stores/selectionStore';
  import { studyProperties, fetchStudyProperties } from '$stores/configStore';
  import { theme } from '$stores/themeStore';
  import { createChartPrintHandler, getDynamicChartOptions } from '$utils/chartPrintUtils';
  import { get } from 'svelte/store';

  export let readings = [];

  // Color configuration
  const colors = {
    veryHigh: '#FFA500',    // Orange
    high: '#FFD54F',        // Lighter Yellow
    target: '#007800',      // Green
    low: '#FF5555',         // Red
    veryLow: '#DC3545'      // Dark Red
  };

  // Base ranges in mg/dL (will be overridden by study properties)
  let baseRanges = {
    veryHigh: 250,
    high: 180,
    targetLow: 70,
    veryLow: 54,
  };  let chart;
  let isInitialized = false;
  let printHandler;

  // Update base ranges from study properties
  $: if ($studyProperties?.glucoseParameters?.thresholds) {
    baseRanges = {
      veryHigh: $studyProperties.glucoseParameters.thresholds.veryHigh,
      high: $studyProperties.glucoseParameters.thresholds.high,
      targetLow: $studyProperties.glucoseParameters.thresholds.low,
      veryLow: $studyProperties.glucoseParameters.thresholds.veryLow,
    };
  }

  // Reactive ranges that update with unit changes
  $: ranges = {
    veryHigh: convertGlucoseValue(baseRanges.veryHigh, $glucoseunit) || 0,
    high: convertGlucoseValue(baseRanges.high, $glucoseunit) || 0,
    targetLow: convertGlucoseValue(baseRanges.targetLow, $glucoseunit) || 0,
    veryLow: convertGlucoseValue(baseRanges.veryLow, $glucoseunit) || 0,
  };

  // Reactive CGM sampling interval from study settings (defaults to 5 minutes)
  $: intervalMinutes = $studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5;

  // Helper function to format range text
  // Use actual mg/dL bounds for conversion (not arithmetic in converted space) to avoid rounding errors
  function getRangeText(range) {
    const unit = $glucoseunit;
    const decimals = isMmol ? 1 : 0;

    switch(range) {
      case 'veryHigh':
        return `Very High (>${ranges.veryHigh.toFixed(decimals)} ${unit})`;
      case 'high':
        return `High (${(convertGlucoseValue(baseRanges.high + 1, unit) ?? 0).toFixed(decimals)}-${ranges.veryHigh.toFixed(decimals)} ${unit})`;
      case 'target':
        return `Target (${ranges.targetLow.toFixed(decimals)}-${ranges.high.toFixed(decimals)} ${unit})`;
      case 'low':
        return `Low (${ranges.veryLow.toFixed(decimals)}-${(convertGlucoseValue(baseRanges.targetLow - 1, unit) ?? 0).toFixed(decimals)} ${unit})`;
      case 'veryLow':
        return `Very Low (<${ranges.veryLow.toFixed(decimals)} ${unit})`;
      default:
        return '';
    }
  }

  // Add these computed values at the script level
  let percentages;
  let timeInHours;

  onMount(async () => {
    // Fetch study properties to get glucose thresholds
    if ($selectedStudy?._id) {
      await fetchStudyProperties($selectedStudy._id);
    }
    
    isInitialized = true;
  });

  // Add new computed values
  let averageGlucose;
  let gmi;
  let glucoseVariability;

  $: isMmol = $glucoseunit === 'mmol/L';

  // Force update when unit changes
  $: unitChanged = $glucoseunit;

  $: {
    percentages = calculateTimeInRanges(readings);
    // Calculate total minutes based on study-configured sampling interval
    const totalMinutes = readings.length * Math.max(1, intervalMinutes);
    timeInHours = percentages.map((p) => {
      if (p === 0.5) return { days: 0, hours: 0, minutes: 0 };
      const minutes = (p * totalMinutes) / 100;
      return {
        days: Math.floor(minutes / (24 * 60)),
        hours: Math.floor((minutes % (24 * 60)) / 60),
        minutes: Math.round(minutes % 60),
      };
    });

    // Calculate Average Glucose
    // Formula: Mean = Σx/n
    // Where Σx is the sum of all glucose values and n is the number of readings
    averageGlucose =
      readings.length > 0 ? readings.reduce((sum, r) => sum + r.value, 0) / readings.length : null;

    // Calculate GMI (Glucose Management Indicator)
    // Formula: GMI (%) = 3.31 + 0.02392 × [mean glucose in mg/dL]
    // Only calculate GMI when there's data
    gmi = readings.length > 0 ? 3.31 + 0.02392 * averageGlucose : null;

    // Calculate Glucose Variability (CV)
    // Formula: CV (%) = (Standard Deviation / Mean) × 100
    // Where Standard Deviation = √(Σ(x - μ)²/(n-1)) for sample standard deviation
    if (readings.length > 1) {
      const variance =
        readings.reduce((sum, r) => {
          return sum + Math.pow(r.value - averageGlucose, 2);
        }, 0) / (readings.length - 1); // Use sample standard deviation (n-1)
      const standardDeviation = Math.sqrt(variance);
      glucoseVariability = (standardDeviation / averageGlucose) * 100;
    } else {
      glucoseVariability = null;
    }
  }

  // Tooltip content
  const tooltips = {
    average: "The arithmetic mean of all glucose readings during the selected period.",
    gmi: "Glucose Management Indicator (GMI) estimates your A1C based on average glucose. Formula: GMI (%) = 3.31 + 0.02392 × [mean glucose in mg/dL]",
    cv: "Coefficient of Variation (CV) measures glucose variability. Values below 36% indicate stable glucose levels."
  };
  
  // Tooltip state
  let activeTooltip = null;
  
  function showTooltip(id) {
    activeTooltip = id;
  }
  
  function hideTooltip() {
    activeTooltip = null;
  }

  function adjustPercentagesForDisplay(percentages) {
    const MIN_PERCENT = 2.5;
    const adjusted = [...percentages];
    let deficit = 0;

    percentages.forEach((p, i) => {
      if (p > 0 && p < MIN_PERCENT) {
        deficit += MIN_PERCENT - p;
        adjusted[i] = MIN_PERCENT;
      }
    });

    if (deficit > 0) {
      const donorIndices = percentages
        .map((p, i) => (p >= MIN_PERCENT ? i : -1))
        .filter((i) => i !== -1);

      const totalDonorValue = donorIndices.reduce((sum, i) => sum + percentages[i], 0);

      if (totalDonorValue > deficit) {
        donorIndices.forEach((i) => {
          const donation = (percentages[i] / totalDonorValue) * deficit;
          adjusted[i] -= donation;
        });
      }
    }
    return adjusted;
  }

  function calculateTimeInRanges(data) {
    const total = data.length;
    if (total === 0) return [0, 0, 0, 0, 0];

    // Convert all readings to current unit for comparison
    const convertedData = data.map(r => ({
      ...r,
      value: convertGlucoseValue(r.value, $glucoseunit) || 0
    }));

    const counts = {
      veryLow: convertedData.filter((r) => r.value < ranges.veryLow).length,
      low: convertedData.filter((r) => r.value < ranges.targetLow && r.value >= ranges.veryLow).length,
      target: convertedData.filter((r) => r.value >= ranges.targetLow && r.value <= ranges.high).length,
      high: convertedData.filter((r) => r.value > ranges.high && r.value <= ranges.veryHigh).length,
      veryHigh: convertedData.filter((r) => r.value > ranges.veryHigh).length,
    };

    // Return array in the correct order: [veryLow, low, target, high, veryHigh]
    return [
      (counts.veryLow / total) * 100,
      (counts.low / total) * 100,
      (counts.target / total) * 100,
      (counts.high / total) * 100,
      (counts.veryHigh / total) * 100
    ];
  }

  function updateChartData() {
    if (!chart || !isInitialized) return;
    
    const originalPercentages = calculateTimeInRanges(readings);
    const displayPercentages = adjustPercentagesForDisplay(originalPercentages);
    
    chart.data.datasets.forEach((dataset, index) => {
      dataset.data = [displayPercentages[index]];
    });
    chart.update('none');
  }  onMount(() => {
    const ctx = document.getElementById('timeInRangeChart');
    
    // Base chart configuration that doesn't change
    const baseOptions = {
      responsive: false,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 50,
          right: 50,
          top: 16,
          bottom: 0
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          barPercentage: 0.8,
          categoryPercentage: 0.9,
        },
        y: {
          stacked: true,
          min: 0,
          max: 100,
          ticks: {
            display: false,
          },
          title: {
            display: false,
          },
          grid: {
            display: false,
            drawBorder: false,
          },
          border: {
            display: false,
          },
          beginAtZero: true,
          suggestedMin: 0,
          suggestedMax: 100,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const originalValue = calculateTimeInRanges(readings)[context.datasetIndex];
              return `${context.dataset.label}: ${originalValue.toFixed(1)}%`;
            },
          },
        },
      },
    };

    const createChart = () => {
      if (chart) {
        chart.destroy();
      }

      const originalPercentages = calculateTimeInRanges(readings);
      const displayPercentages = adjustPercentagesForDisplay(originalPercentages);

      const datasets = [
        { label: 'Very Low', color: colors.veryLow },
        { label: 'Low', color: colors.low },
        { label: 'Target', color: colors.target },
        { label: 'High', color: colors.high },
        { label: 'Very High', color: colors.veryHigh },
      ].map((item, index) => {
        const percentage = displayPercentages[index];
        return {
          label: item.label,
          data: [percentage],
          backgroundColor: item.color,
          borderWidth: 0,
        };
      });      const isPrintMode = printHandler ? get(printHandler.isPrintMode) : false;
      const chartOptions = getDynamicChartOptions({ baseOptions }, isPrintMode);

      chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['Time in Range'],
          datasets: datasets,
        },
        options: chartOptions,
      });
      
      // Force resize to larger canvas but same bar size
      chart.resize(200, 400);
    };

    // Initialize print handler
    printHandler = createChartPrintHandler(
      createChart,
      () => chart && isInitialized
    );

    // Create initial chart
    createChart();
      isInitialized = true;

    // Cleanup function
    return () => {
      if (printHandler) {
        printHandler.cleanup();
      }
    };
  });

  // Handle data changes - only update if initialized
  $: if (isInitialized && readings && readings.length > 0 && $glucoseunit) {
    updateChartData();
  }
</script>

<div class="flex flex-row pb-5 items-end pt-6 relative">  <div class="absolute left-0 h-100">
    <canvas id="timeInRangeChart" class="print:bg-white" style="height: 100%; width: 100%"></canvas>
  </div>
  <div class="ml-52 w-3/5 flex items-start justify-center" style="height: 400px;">
    <table class="text-white text-xs">
      <tbody>
        <!-- Add new rows at the top -->
        <tr>
          <td class="pt-2 text-black dark:text-white text-lg relative">
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <span 
              class="cursor-help" 
              on:mouseenter={() => showTooltip('average')} 
              on:mouseleave={hideTooltip}>
              Average: <span class="font-bold">{averageGlucose ? convertGlucoseValue(averageGlucose, $glucoseunit)?.toFixed(isMmol ? 1 : 0) : '-'}</span> {averageGlucose ? $glucoseunit : ''}
            </span>
            {#if activeTooltip === 'average'}
              <div class="absolute z-10 bg-gray-800 text-white p-2 rounded text-xs w-64 -left-32 top-8">
                {tooltips.average}
              </div>
            {/if}
          </td>
        </tr>
        <tr>
          <td class="py-0 text-black dark:text-white text-lg relative">
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <span 
              class="cursor-help" 
              on:mouseenter={() => showTooltip('gmi')} 
              on:mouseleave={hideTooltip}>
              GMI: <span class="font-bold">{gmi ? gmi.toFixed(1) : '-'}</span>{gmi ? '%' : ''}
            </span>
            {#if activeTooltip === 'gmi'}
              <div class="absolute z-10 bg-gray-800 text-white p-2 rounded text-xs w-64 -left-32 top-6">
                {tooltips.gmi}
              </div>
            {/if}
          </td>
        </tr>
        <tr>
          <td class="py-0 text-black dark:text-white text-lg relative">
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <span 
              class="cursor-help" 
              on:mouseenter={() => showTooltip('cv')} 
              on:mouseleave={hideTooltip}>
              CV: <span class="font-bold">{glucoseVariability ? glucoseVariability.toFixed(1) : '-'}</span>{glucoseVariability ? '%' : ''}
            </span>
            {#if activeTooltip === 'cv'}
              <div class="absolute z-10 bg-gray-800 text-white p-2 rounded text-xs w-64 -left-32 top-6">
                {tooltips.cv}
              </div>
            {/if}
          </td>
        </tr>
        <tr><td class="py-3"></td></tr>
        <!-- Spacer -->
        <tr>
          <td class="whitespace-nowrap align-middle py-1 text-black dark:text-white">
            {#key unitChanged}
              {getRangeText('veryHigh')}<br />
            {/key}
            <span class="text-black dark:text-white text-lg font-bold">
              {(percentages?.[4] === 0.5 ? 0 : percentages?.[4])?.toFixed(1)}% 
              {#if timeInHours?.[4]?.days > 0 || timeInHours?.[4]?.hours > 0 || timeInHours?.[4]?.minutes > 0}
                •
                {#if timeInHours?.[4]?.days > 0}{timeInHours[4].days}d
                {/if}
                {#if timeInHours?.[4]?.hours > 0}{timeInHours[4].hours}h
                {/if}
                {timeInHours?.[4]?.minutes}m
              {/if}
            </span>
          </td>
        </tr>
        <tr>
          <td class="whitespace-nowrap align-middle py-1 text-black dark:text-white">
            {#key unitChanged}
              {getRangeText('high')}<br />
            {/key}
            <span class="text-black dark:text-white text-lg font-bold">
              {(percentages?.[3] === 0.5 ? 0 : percentages?.[3])?.toFixed(1)}% 
              {#if timeInHours?.[3]?.days > 0 || timeInHours?.[3]?.hours > 0 || timeInHours?.[3]?.minutes > 0}
                •
                {#if timeInHours?.[3]?.days > 0}{timeInHours[3].days}d
                {/if}
                {#if timeInHours?.[3]?.hours > 0}{timeInHours[3].hours}h
                {/if}
                {timeInHours?.[3]?.minutes}m
              {/if}
            </span>
          </td>
        </tr>
        <tr>
          <td class="whitespace-nowrap align-middle py-1 text-black dark:text-white">
            {#key unitChanged}
              {getRangeText('target')}<br />
            {/key}
            <span class="text-black dark:text-white text-lg font-bold">
              {(percentages?.[2] === 0.5 ? 0 : percentages?.[2])?.toFixed(1)}% 
              {#if timeInHours?.[2]?.days > 0 || timeInHours?.[2]?.hours > 0 || timeInHours?.[2]?.minutes > 0}
                •
                {#if timeInHours?.[2]?.days > 0}{timeInHours[2].days}d
                {/if}
                {#if timeInHours?.[2]?.hours > 0}{timeInHours[2].hours}h
                {/if}
                {timeInHours?.[2]?.minutes}m
              {/if}
            </span>
          </td>
        </tr>
        <tr>
          <td class="whitespace-nowrap align-middle py-1 text-black dark:text-white">
            {#key unitChanged}
              {getRangeText('low')}<br />
            {/key}
            <span class="text-black dark:text-white text-lg font-bold">
              {(percentages?.[1] === 0.5 ? 0 : percentages?.[1])?.toFixed(1)}% 
              {#if timeInHours?.[1]?.days > 0 || timeInHours?.[1]?.hours > 0 || timeInHours?.[1]?.minutes > 0}
                •
                {#if timeInHours?.[1]?.days > 0}{timeInHours[1].days}d
                {/if}
                {#if timeInHours?.[1]?.hours > 0}{timeInHours[1].hours}h
                {/if}
                {timeInHours?.[1]?.minutes}m
              {/if}
            </span>
          </td>
        </tr>
        <tr>
          <td class="whitespace-nowrap align-middle py-1 text-black dark:text-white">
            {#key unitChanged}
              {getRangeText('veryLow')}<br />
            {/key}
            <span class="text-black dark:text-white text-lg font-bold">
              {(percentages?.[0] === 0.5 ? 0 : percentages?.[0])?.toFixed(1)}%
              {#if timeInHours?.[0]?.days > 0 || timeInHours?.[0]?.hours > 0 || timeInHours?.[0]?.minutes > 0}
                •
                {#if timeInHours?.[0]?.days > 0}{timeInHours[0].days}d
                {/if}
                {#if timeInHours?.[0]?.hours > 0}{timeInHours[0].hours}h
                {/if}
                {timeInHours?.[0]?.minutes}m
              {/if}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- <div class="w-full h-full pl-10 pr-10">
    <canvas id="timeInRangeChart"></canvas>
  </div> -->

<style>
  canvas {
    max-width: 100%;
    height: 100%;
  }
</style>
